import React, { useState, useRef } from "react";
import { Formik, Form, Field } from "formik";
import { Box, Typography, Divider } from "@material-ui/core";
import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import CustomDatePicker from "components/Input/CustomDatePicker";
import SelectField from "components/Select/SelectField";
import AppColor from "utilities/AppColor";
import { useDispatch } from "react-redux";
import { submitIntakeLinkNdisDetails } from "store/slices/client";
import { showToast } from "store/slices/toast";
import * as Yup from "yup";
import moment from "moment";
import { useTranslation } from "react-i18next";

const CustomFileUploadBox = ({ label, file, initialFileName, onFileSelect }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  return (
    <Box display="flex" alignItems="center" p={1.5} bgcolor="#F9FAFB" border="1px solid #E5E7EB" borderRadius={6}>
      <i className="fas fa-file-pdf" style={{ fontSize: 18, color: "#EF4444", marginRight: 8 }} />
      <Box flex={1} textOverflow="ellipsis" whiteSpace="nowrap" fontSize={13} color="#374151" overflow="hidden">
        {file ? file.name : initialFileName ? initialFileName : t("text.no_file_selected")}
      </Box>
      <AppButton
        label={file || initialFileName ? t("button.change") : label}
        mode="outlined"
        rounder
        style={{ fontSize: 11, textTransform: "none", height: 28, marginLeft: 8 }}
        onClick={() => {
          if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
          }
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files[0];
          if (f) onFileSelect(f);
        }}
      />
    </Box>
  );
};

const IntakeLinkNdisDetails = ({ clientData, token, onNextStep }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const ndisDocument = clientData?.clientDocumentResponses?.find(doc => doc.fileCategory === "NDIS");
  const ndisDocDetail = ndisDocument?.clientDocumentDetailResponse?.[0];

  const initialValues = {
    ndisDocumentId: ndisDocument?.id || null,
    ndisPlanNumber: clientData?.ndisPlanNumber || "",
    planStartDate: clientData?.planStartDate || null,
    planEndDate: clientData?.planEndDate || null,
    billingDetail: clientData?.billingDetail || "",
    ndisPlanFile: null,
    ndisDocumentName: ndisDocument?.fileName || "",
    ndisDocumentReviewDueDate: ndisDocDetail?.reviewDueDate ? moment(ndisDocDetail?.reviewDueDate).format("YYYY/MM/DD") : "",
    
  };
  const validationSchema = Yup.object().shape({
    billingDetail: Yup.string().required(t("validation.required")),
    ndisPlanNumber: Yup.string().max(255, "validation.max_length_255"),
    ndisDocumentName: Yup.string()
      .max(255, "validation.max_length_255")
      .test("is-required", t("validation.required"), function(value) {
        const { ndisPlanFile, ndisDocumentId } = this.parent;
        if (ndisPlanFile || ndisDocumentId) {
          return !!value;
        }
        return true;
      }),
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    const formData = new FormData();
    const payload = {
      intake_link_token: token,
      ndis_plan_number: values.ndisPlanNumber,
      plan_start_date: values.planStartDate,
      plan_end_date: values.planEndDate,
      billing_detail: values.billingDetail,
      ndis_document_id: values.ndisDocumentId,
      ndis_document_name: values.ndisDocumentName,
      ndis_document_review_due_date: values.ndisDocumentReviewDueDate,
    };
    
    formData.append("content-data", new Blob([JSON.stringify(payload)], { type: "application/json" }));
    if (values.ndisPlanFile) {
      formData.append("ndisPlanFile", values.ndisPlanFile);
    }

    try {
      const response = await dispatch(submitIntakeLinkNdisDetails(formData));
      if (response && response.payload) {
        onNextStep(true);
      }
    } catch (e) {
      console.log(e);
      dispatch(showToast({ message: "Failed to submit NDIS details", type: "error" }));
    }
    setLoading(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, setFieldValue, values }) => {
        const hasDocument = !!values.ndisPlanFile || !!values.ndisDocumentId;
        return (
          <Form>
          <Typography variant="h6" style={{ color: AppColor.mainColor, marginBottom: 15 }}>
            {t("label.ndis_details")}
          </Typography>
          <Divider style={{ marginBottom: 20 }} />
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="ndisPlanNumber" label={t("label.ndisPlanNumber")} />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field
                component={SelectField}
                label={t("label.plan_management")}
                name="billingDetail"
                required
                options={[
                  { value: "NDIA", label: t("label.ndia_managed") },
                  { value: "PLAN_MANAGER", label: t("label.plan_managed") },
                  { value: "SELF", label: t("label.self_managed") }
                ]}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field
                component={CustomDatePicker}
                label={t("label.plan_start_date")}
                name="planStartDate"
                type="text"
                formatDateJob={true}
                classInput="date-picker-container-custom"
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field
                component={CustomDatePicker}
                label={t("label.plan_end_date")}
                name="planEndDate"
                type="text"
                formatDateJob={true}
                classInput="date-picker-container-custom"
              />
            </div>
          </div>
          {/* <div className="row">
            <div className="col-sm-12 col-md-12">
              <Field component={AppInput} name="fundingCategories" label="Funding Categories" />
            </div>
          </div> */}
          <div className="row">
            <div className="col-sm-12 col-md-12">
              <Box mt={2}>
                <Typography variant="subtitle2" style={{ marginBottom: 10 }}>{t("label.ndis_plan_document")}</Typography>
                <div className="row">
                  <div className="col-sm-12 col-md-12" style={{ marginBottom: 15 }}>
                    <CustomFileUploadBox
                      label={t("button.upload_ndis_plan")}
                      onFileSelect={(f) => setFieldValue("ndisPlanFile", f)}
                      file={values.ndisPlanFile}
                      initialFileName={ndisDocDetail?.fileS3Name}
                    />
                  </div>
                  {hasDocument && (
                    <>
                      <div className="col-sm-12 col-md-6">
                        <Field component={AppInput} name="ndisDocumentName" label={t("label.document_name")} required style={{ marginBottom: 10 }} />
                      </div>
                      <div className="col-sm-12 col-md-6">
                        <Field
                          component={CustomDatePicker}
                          label={t("label.review_due_date")}
                          name="ndisDocumentReviewDueDate"
                          type="text"
                          formatDateJob={true}
                          custom={{ disablePast: true }}
                          classInput="date-picker-container-custom"
                        />
                      </div>
                    </>
                  )}
                </div>
              </Box>
            </div>
          </div>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <AppButton
              label={t("button.saveAndNext")}
              type="submit"
              buttonColor={AppColor.mainColor}
              isLoading={loading}
              disabled={!isValid || loading}
              style={{ width: "150px" }}
            />
          </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default IntakeLinkNdisDetails;
